import {
  ConnectionType,
  useMarketAccountsContext,
  useMarketsContext,
  useSecuritiesContext,
  useStrategiesContext,
  type Security,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { useFixingIndices } from '../providers/FixingIndexProvider';
import { useMarketAccountPermissionFilters } from '../providers/MarketAccountPermissionFiltersProvider';
import { useTradingSettings } from '../providers/TradingSettingsContext';
import { useFeatureFlag } from './useFeatureFlag';
import { getTradableMarketAccountNames } from './useTradableMarketAccountNames';

/**
 * Returns a getter for tradable market account names, configured for multileg (Delta1/SyntheticCross) Orders flow.
 * Use when passing to LegsContainer.updateSecurityForLeg or updateSymbol for auto-select when only one market account exists.
 */
export function useGetTradableMarketAccountNamesForMultileg(): (security: Security) => string[] {
  const { enableAccountSegregation } = useFeatureFlag();
  const { marketsByName } = useMarketsContext();
  const { marketAccountsByMarket, marketAccountsList } = useMarketAccountsContext();
  const { allowSyntheticCrosses, allowSyntheticCcy: allowSyntheticCrossCurrency } = useTradingSettings();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { strategiesByName } = useStrategiesContext();
  const { marketsByFixingIndex } = useFixingIndices();
  const { marketAccountFilterIDsBySubAccountName, marketAccountPermissionFiltersByFilterID } =
    useMarketAccountPermissionFilters();

  return useCallback(
    (security: Security) =>
      getTradableMarketAccountNames({
        connectionType: ConnectionType.Orders,
        subAccountAllocations: -1, // never apply trading acct segregation
        combineAllMarketAccounts: true,
        enableAccountSegregation,
        marketAccountsList,
        marketAccountFilterIDsBySubAccountName,
        marketAccountPermissionFiltersByFilterID,
        security,
        allowSyntheticCrosses,
        ulResponse: undefined,
        marketsByName,
        marketAccountsByMarket,
        allowSyntheticCrossCurrency,
        securitiesBySymbol,
        strategiesByName,
        marketsByFixingIndex,
      }),
    [
      enableAccountSegregation,
      marketAccountsList,
      marketAccountFilterIDsBySubAccountName,
      marketAccountPermissionFiltersByFilterID,
      allowSyntheticCrosses,
      marketsByName,
      marketAccountsByMarket,
      allowSyntheticCrossCurrency,
      securitiesBySymbol,
      strategiesByName,
      marketsByFixingIndex,
    ]
  );
}
